import React, { useEffect, useMemo, useState } from "react";
import {
  Container, Navbar, Nav, Button, Offcanvas, Badge,
  OverlayTrigger, Tooltip, Dropdown, ButtonGroup, Form, InputGroup
} from "react-bootstrap";
import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { motion, AnimatePresence, useScroll, useSpring, useTransform } from "framer-motion";
import { useAuth } from "../../context/AuthContext";
import useSchool from "../../hooks/useSchool";
import { useTheme } from "../../context/ThemeContext";

/* ───────────────── 3D PNG ICONS (adjust/extend as you add files) ───────────── */
import PNG_Profile     from "../../assets/3d/profile.png";
import PNG_Classes     from "../../assets/3d/online-class.png";
import PNG_Assignments from "../../assets/3d/assignments.png";
import PNG_Progress    from "../../assets/3d/progress.png";    // used for Student Results
import PNG_Exams       from "../../assets/3d/exams.png";
import PNG_Fees        from "../../assets/3d/fees.png";
import PNG_Attendance  from "../../assets/3d/attendance.png";
import PNG_Megaphone   from "../../assets/3d/megaphone.png";
import PNG_Dashboard from "../../assets/3d/dashboard.png";
import PNG_Teachers   from "../../assets/3d/teacher.png";
import PNG_Students   from "../../assets/3d/roster.png";
import PNG_Support    from "../../assets/3d/support.png";
import PNG_Settings   from "../../assets/3d/settings.png";
/* If you have more, import them and add `img:` in LINKS below:
   
*/

/* ----------------------------- Accent utilities ---------------------------- */
const hexToRgb = (hex) => {
  if (!hex) return [13,110,253];
  let h = hex.replace("#","").trim();
  if (h.length === 3) h = h.split("").map(c => c + c).join("");
  if (h.length === 8) h = h.slice(0,6);
  const num = parseInt(h, 16);
  return [ (num>>16)&255, (num>>8)&255, num&255 ];
};
const withAlpha = (hex, a=0.18) => {
  const [r,g,b] = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
};
function useAccent(vars) {
  const [accent, setAccent] = useState(
    () => localStorage.getItem("accentColor") || vars.primary || "#0d6efd"
  );
  useEffect(() => {
    const root = document.documentElement.style;
    root.setProperty("--accent", accent);
    root.setProperty("--accent-04", withAlpha(accent, 0.04));
    root.setProperty("--accent-10", withAlpha(accent, 0.10));
    root.setProperty("--accent-18", withAlpha(accent, 0.18));
    root.setProperty("--accent-30", withAlpha(accent, 0.30));
    localStorage.setItem("accentColor", accent);
  }, [accent]);
  return [accent, setAccent];
}

// Dominant color from image (quick & tiny)
const getDominantColorFromImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const w = 64, h = 64;
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      ctx.drawImage(img, 0, 0, w, h);
      const { data } = ctx.getImageData(0, 0, w, h);

      const buckets = new Map();
      for (let i = 0; i < data.length; i += 4) {
        const a = data[i + 3];
        if (a < 128) continue;
        const r = Math.round(data[i] / 24) * 24;
        const g = Math.round(data[i + 1] / 24) * 24;
        const b = Math.round(data[i + 2] / 24) * 24;
        const key = `${r},${g},${b}`;
        buckets.set(key, (buckets.get(key) || 0) + 1);
      }
      if (!buckets.size) return resolve("#6c757d");
      let best = null, max = -1;
      for (const [k, v] of buckets.entries()) if (v > max) { max = v; best = k; }
      const [r, g, b] = best.split(",").map(Number);
      resolve(`#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`);
    };
    img.onerror = reject;
    img.src = src;
  });

const userHasAccentChoice = () => !!localStorage.getItem("accentColor");

/* -------------------------------- Nav config --------------------------------
   Add `img` to any item to use a 3D PNG (preferred). If `img` is missing,
   the Bootstrap icon class in `icon` is used as a fallback.
------------------------------------------------------------------------------- */
const LINKS = [
  { to: "/school", label: "Dashboard", icon: "bi-grid-1x2", exact: true,  img: PNG_Dashboard },
  { to: "/school/profile", label: "Profile", icon: "bi-person-badge", img: PNG_Profile },
  { to: "/school/fees/login", label: "Fees", icon: "bi-cash-coin", img: PNG_Fees },
  { to: "/school/exams", label: "Exams", icon: "bi-journal-text", exact: true, img: PNG_Exams },
  { to: "/school/exams/studentresults", label: "Student Results", icon: "bi-bar-chart", img: PNG_Progress },
  { to: "/school/students", label: "Students", icon: "bi-people" , img: PNG_Students },
  { to: "/school/attendance", label: "Attendance", icon: "bi-calendar-check", img: PNG_Attendance },
  { to: "/school/teachers", label: "Teachers", icon: "bi-person-badge", img: PNG_Teachers },
  { to: "/school/onlineclasses", label: "Online Classes", icon: "bi-camera-video", img: PNG_Classes },
  { to: "/school/assignments", label: "Assignments", icon: "bi-clipboard-check", img: PNG_Assignments },
  { to: "/school/circulars", label: "Circulars", icon: "bi-megaphone", img: PNG_Megaphone },
  { to: "/school/support", label: "Support", icon: "bi-life-preserver", img: PNG_Support  },
  { to: "/school/settings", label: "Settings", icon: "bi-gear" , img: PNG_Settings  },
];

const SIDEBAR_KEY = "schoolSidebar.open";

/* ---------------------------- Motion variants ---------------------------- */
const liVariants = {
  hidden: { opacity: 0, x: -6 },
  show: i => ({ opacity: 1, x: 0, transition: { delay: 0.02 * i, duration: 0.25 } }),
  hover: { x: 4, transition: { type: "spring", stiffness: 240, damping: 18 } },
};

/* -------------------------------- Component -------------------------------- */
export default function SchoolLayout() {
  const { profile, logout, signOut, user } = useAuth();
  const { school } = useSchool();
  const doLogout = logout || signOut;
  const navigate = useNavigate();
  const location = useLocation();

  const { theme, toggleTheme } = useTheme(); // "light" | "dark"
  const [accent, setAccent] = useAccent({ primary: "#0d6efd" });

  // Sidebar open state
  const [open, setOpen] = useState(true);
  useEffect(() => {
    const saved = localStorage.getItem(SIDEBAR_KEY);
    if (saved != null) setOpen(saved === "1");
  }, []);
  useEffect(() => {
    localStorage.setItem(SIDEBAR_KEY, open ? "1" : "0");
  }, [open]);

  // Mobile drawer
  const [drawer, setDrawer] = useState(false);

  // Quick find
  const [q, setQ] = useState("");

  // Title / Logo
  const schoolTitle = useMemo(
    () => school?.name || profile?.schoolName || profile?.school?.name || "School",
    [school, profile]
  );
  const logoUrl = school?.logoUrl || school?.logoURL || school?.logo?.url;

  // Auto-accent from logo (respect manual choice)
  useEffect(() => {
    if (!logoUrl || userHasAccentChoice()) return;
    let alive = true;
    getDominantColorFromImage(logoUrl)
      .then((hex) => { if (alive) setAccent(hex); })
      .catch(()=>{});
    return () => { alive = false; };
  }, [logoUrl, setAccent]);

  const logoNode = useMemo(() => {
    if (logoUrl) {
      return (
        <img
          src={logoUrl}
          alt={`${schoolTitle} logo`}
          style={{
            width: 40, height: 40, borderRadius: 12, objectFit: "cover",
            border: "1px solid var(--bs-border-color, #e9ecef)"
          }}
        />
      );
    }
    return (
      <div
        className="shadow-sm"
        style={{
          width: 40, height: 40, borderRadius: 12,
          background: "var(--accent-10)", color: "var(--accent)",
          fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center",
          border: "1px solid var(--bs-border-color, #e9ecef)"
        }}
        aria-hidden
      >
        {(schoolTitle || "S").slice(0, 1).toUpperCase()}
      </div>
    );
  }, [logoUrl, schoolTitle]);

  const confirmLogout = async () => {
    const dark = theme === "dark";
    const res = await Swal.fire({
      title: "Sign out?",
      html: `
        <div style="text-align:left;line-height:1.5">
          <div>You’ll be signed out of <b>School Admin</b>.</div>
          <div style="font-size:12px;margin-top:6px;opacity:.8;">
            ${user?.email ? `Signed in as <code>${user.email}</code>` : ""}
          </div>
        </div>
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Logout",
      cancelButtonText: "Stay",
      reverseButtons: true,
      confirmButtonColor: "#dc3545",
      focusCancel: true,
      background: dark ? "#15171b" : "#ffffffff",
      color: dark ? "#e9ecef" : "#212529",
    });
    if (!res.isConfirmed) return;
    try {
      await doLogout();
      navigate("/login", { replace: true });
    } catch (e) {
      Swal.fire({
        icon: "error",
        title: "Logout failed",
        text: e?.message || "Please try again.",
        background: dark ? "#15171b" : "#fff",
        color: dark ? "#e9ecef" : "#212529",
      });
    }
  };

  /* --------------------------- Ripple helper --------------------------- */
  const spawnRipple = (e) => {
    const host = e.currentTarget;
    const r = document.createElement("span");
    const rect = host.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    r.className = "ripple";
    r.style.width = r.style.height = `${size}px`;
    r.style.left = `${e.clientX - rect.left - size / 2}px`;
    r.style.top = `${e.clientY - rect.top - size / 2}px`;
    host.appendChild(r);
    r.addEventListener("animationend", () => r.remove());
  };

  /* ------------------------- Scroll progress bar ------------------------- */
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 100, damping: 20, mass: 0.2 });
  const progressScale = useTransform(progress, v => Math.max(0.005, v)); // never 0 width

  /* ------------------------------ Sidebar nav ------------------------------ */
  const SidebarNav = ({ collapsed = false, onNavigate }) => (
    <Nav as="ul" className="flex-column py-2 px-2">
      {LINKS.map((l, i) => {
        const LinkInner = ({ isActive }) => (
          <motion.div
            className={[
              "sidebar-link d-flex align-items-center gap-3 rounded-3 position-relative",
              isActive ? "active" : ""
            ].join(" ")}
            onMouseDown={spawnRipple}
            variants={liVariants}
            custom={i}
            initial="hidden"
            animate="show"
            whileHover="hover"
          >
            <span className="active-rail" aria-hidden />
            <span className="icon-chip d-inline-flex align-items-center justify-content-center">
              {l.img ? (
                <img src={l.img} alt="" className="icon3d" loading="lazy" />
              ) : (
                <i className={`bi ${l.icon}`} />
              )}
            </span>
            {!collapsed && <span className="label text-truncate">{l.label}</span>}
            <span className="shine" aria-hidden />
          </motion.div>
        );

        const content = (
          <Nav.Link
            as={NavLink}
            end={l.exact}
            to={l.to}
            onClick={() => onNavigate?.()}
            className="p-0"
          >
            {({ isActive }) => <LinkInner isActive={isActive} />}
          </Nav.Link>
        );

        return (
          <Nav.Item as="li" key={l.to} className="mb-1">
            {collapsed ? (
              <OverlayTrigger placement="right" overlay={<Tooltip>{l.label}</Tooltip>}>
                <div>{content}</div>
              </OverlayTrigger>
            ) : (
              content
            )}
          </Nav.Item>
        );
      })}
    </Nav>
  );

  /* ------------------------------- Breadcrumbs ------------------------------ */
  const crumbs = useMemo(() => {
    const parts = location.pathname.replace(/^\/+/, "").split("/").filter(Boolean);
    const labels = parts.map((p) => p.replace(/^\w/, (c) => c.toUpperCase()));
    return labels.slice(0, 3);
  }, [location.pathname]);

  /* ---------------------------- Accent palette UI --------------------------- */
  const ACCENTS = [
    "#0d6efd", "#6f42c1", "#d63384", "#fd7e14",
    "#20c997", "#198754", "#e01e37", "#3a86ff",
  ];

  return (
    <div className="d-flex layout-root" style={{ minHeight: "100vh" }}>
      {/* Scroll progress */}
      <motion.div
        style={{
          position: "fixed", insetInlineStart: 0, top: 0, height: 3, transformOrigin: "0% 50%",
          background: "linear-gradient(90deg, var(--accent-18), var(--accent))",
          scaleX: progressScale, zIndex: 2000
        }}
      />

      <div className="bg-waves" aria-hidden />

      {/* Desktop sidebar */}
      <motion.aside
        className="border-end bg-body d-none d-lg-flex flex-column glass-sidebar"
        style={{ width: open ? 260 : 92 }}
        animate={{ width: open ? 260 : 92 }}
        transition={{ type: "spring", stiffness: 220, damping: 24 }}
      >
        {/* Header */}
        <div className="px-3 py-3 border-bottom d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-2 overflow-hidden">
            {logoNode}
            <motion.div
              initial={false}
              animate={{ opacity: open ? 1 : 0, x: open ? 0 : -6 }}
              transition={{ duration: .2 }}
              className="d-flex flex-column"
              style={{ pointerEvents: open ? "auto" : "none" }}
            >
              <span className="fw-bold text-truncate" title={schoolTitle}>
                {schoolTitle}
              </span>
              <small className="text-muted text-truncate" title={user?.email || ""}>
                {user?.email || "—"}
              </small>
            </motion.div>
          </div>

          <div className="d-flex align-items-center gap-1">
            <Dropdown as={ButtonGroup}>
              <Button
                size="sm"
                variant="light"
                onClick={toggleTheme}
                title={`Switch to ${theme === "light" ? "dark" : "light"} mode`}
              >
                <i className={`bi ${theme === "light" ? "bi-moon" : "bi-sun"}`} />
              </Button>
              <Dropdown.Toggle split size="sm" variant="light" />
              <Dropdown.Menu align="end" className="p-2" style={{ minWidth: 260 }}>
                <Dropdown.Header className="small">Appearance</Dropdown.Header>

                {/* Presets */}
                <div className="d-flex flex-wrap gap-2 mb-2">
                  {ACCENTS.map((c) => (
                    <button
                      key={c}
                      className="accent-dot"
                      style={{ background: c, outline: c === accent ? `2px solid ${c}` : "none" }}
                      onClick={() => setAccent(c)}
                      title={c}
                    />
                  ))}
                </div>

                {/* Custom + Auto from logo */}
                <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                  <label className="accent-picker mb-0">
                    <input
                      type="color"
                      value={accent}
                      onChange={(e) => setAccent(e.target.value)}
                      aria-label="Pick custom accent color"
                    />
                    <span className="swatch" style={{ background: accent }} />
                    <span className="small ms-2">Custom</span>
                  </label>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    disabled={!logoUrl}
                    onClick={async () => {
                      if (!logoUrl) return;
                      try {
                        const hex = await getDominantColorFromImage(logoUrl);
                        setAccent(hex);
                      } catch {}
                    }}
                  >
                    <i className="bi bi-magic" /> Auto from logo
                  </Button>
                </div>

                <Dropdown.Divider />
                <Dropdown.Item onClick={() => setOpen((v) => !v)}>
                  <i className="bi bi-layout-sidebar-inset me-2" />
                  {open ? "Collapse sidebar" : "Expand sidebar"}
                </Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item onClick={confirmLogout} className="text-danger">
                  <i className="bi bi-box-arrow-right me-2" />
                  Logout
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>

        {/* Nav */}
        <SidebarNav collapsed={!open} />

        {/* Footer */}
        <div className="mt-auto p-3 border-top d-flex align-items-center justify-content-between">
          <motion.div
            initial={false}
            animate={{ opacity: open ? 1 : 0, x: open ? 0 : -6 }}
            transition={{ duration: .2 }}
            className="d-flex flex-column"
            style={{ pointerEvents: open ? "auto" : "none" }}
          >
            <span className="small text-muted">Plan</span>
            <Badge bg="light" text="dark" className="border mt-1">
              {(school?.plan || "free").toString().toUpperCase()}
            </Badge>
          </motion.div>

          <Button
            variant="outline-secondary"
            className="d-flex align-items-center justify-content-center gap-2"
            onClick={confirmLogout}
            size="sm"
          >
            <i className="bi bi-box-arrow-right" />
            {open && <span>Logout</span>}
          </Button>
        </div>
      </motion.aside>

      {/* Content */}
      <div className="flex-grow-1 d-flex flex-column">
        {/* Desktop topbar */}
        <motion.div
          className="topbar d-none d-lg-flex align-items-center justify-content-between border-bottom sticky-top px-3"
          initial={{ opacity: .85, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: .18 }}
        >
          <div className="d-flex align-items-center gap-3">
            <Button
              size="sm"
              variant="outline-secondary"
              className="rounded-3"
              onClick={() => setOpen((v) => !v)}
              title={open ? "Collapse sidebar" : "Expand sidebar"}
            >
              <i className="bi bi-layout-sidebar" />
            </Button>

            <div className="d-flex align-items-center gap-2 small text-muted">
              {crumbs.map((c, i) => (
                <span key={i} className="d-inline-flex align-items-center gap-2">
                  {i > 0 && <i className="bi bi-chevron-right opacity-50" />}
                  <span className={i === crumbs.length - 1 ? "fw-semibold text-body" : ""}>
                    {c}
                  </span>
                </span>
              ))}
            </div>
          </div>

          <div className="d-flex align-items-center gap-2">
            <Form
              onSubmit={(e) => {
                e.preventDefault();
                if (!q.trim()) return;
                navigate(`/school?query=${encodeURIComponent(q.trim())}`);
              }}
              className="d-none d-xl-block"
            >
              <InputGroup className="quick-input">
                <InputGroup.Text><i className="bi bi-search" /></InputGroup.Text>
                <Form.Control
                  placeholder="Quick find…"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
                {q && (
                  <Button variant="outline-secondary" onClick={() => setQ("")} title="Clear">
                    <i className="bi bi-x" />
                  </Button>
                )}
              </InputGroup>
            </Form>

            <OverlayTrigger placement="bottom" overlay={<Tooltip>Toggle theme</Tooltip>}>
              <Button size="sm" variant="outline-secondary" onClick={toggleTheme}>
                <i className={`bi ${theme === "light" ? "bi-moon" : "bi-sun"}`} />
              </Button>
            </OverlayTrigger>

            <Dropdown align="end">
              <Dropdown.Toggle variant="outline-secondary" size="sm" className="d-flex align-items-center gap-2 rounded-3">
                <span className="accent-pill" style={{ background: accent }} />
                <span className="d-none d-md-inline text-truncate" style={{ maxWidth: 160 }}>
                  {user?.email || "Account"}
                </span>
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Header className="small">
                  {schoolTitle}
                  <div className="text-muted">{user?.email}</div>
                </Dropdown.Header>

                {/* Quick accent grid */}
                <Dropdown.ItemText>
                  <div className="small text-muted mb-1">Accent</div>
                  <div className="d-flex flex-wrap gap-2 mb-2">
                    {ACCENTS.map((c) => (
                      <button
                        key={c}
                        className="accent-dot"
                        style={{ background: c, outline: c === accent ? `2px solid ${c}` : "none" }}
                        onClick={() => setAccent(c)}
                        title={c}
                      />
                    ))}
                  </div>

                  <div className="d-flex align-items-center justify-content-between gap-2">
                    <label className="accent-picker mb-0">
                      <input type="color" value={accent} onChange={(e) => setAccent(e.target.value)} aria-label="Pick custom accent color" />
                      <span className="swatch" style={{ background: accent }} />
                      <span className="small ms-2">Custom</span>
                    </label>
                    <Button size="sm" variant="outline-secondary" disabled={!logoUrl}
                      onClick={async ()=>{ try{ const hex=await getDominantColorFromImage(logoUrl); setAccent(hex);}catch{}}}>
                      <i className="bi bi-magic" /> Auto from logo
                    </Button>
                  </div>
                </Dropdown.ItemText>

                <Dropdown.Divider />
                <Dropdown.Item as={NavLink} to="/school/settings">
                  <i className="bi bi-gear me-2" />
                  Settings
                </Dropdown.Item>
                <Dropdown.Item onClick={confirmLogout} className="text-danger">
                  <i className="bi bi-box-arrow-right me-2" />
                  Logout
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </motion.div>

        {/* Mobile topbar */}
        <Navbar bg="body" className="border-bottom d-lg-none sticky-top">
          <Container fluid className="px-2">
            <div className="d-flex align-items-center gap-2">
              <Button variant="primary" size="sm" onClick={() => setDrawer(true)} aria-label="Open menu" className="rounded-3">
                <i className="bi bi-list" />
              </Button>
              <Navbar.Brand className="fw-bold mb-0 d-flex align-items-center gap-2">
                {logoNode}
                <span className="text-truncate" style={{ maxWidth: 180 }}>{schoolTitle}</span>
                <Badge bg="light" text="dark" className="ms-1 border">Admin</Badge>
              </Navbar.Brand>
            </div>
            <div className="d-flex align-items-center gap-2">
              <Button size="sm" variant="outline-secondary" onClick={toggleTheme} className="rounded-3">
                <i className={`bi ${theme === "light" ? "bi-moon" : "bi-sun"}`} />
              </Button>
              <Button size="sm" onClick={confirmLogout} variant="outline-secondary" className="rounded-3">
                <i className="bi bi-box-arrow-right" />
              </Button>
            </div>
          </Container>
        </Navbar>

        <div className="accent-bar d-none d-lg-block" />

        <main className="p-3 p-lg-4 content-shell">
          <Outlet />
        </main>
      </div>

      {/* Mobile drawer with motion fade backdrop */}
      <AnimatePresence>
        {drawer && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <Offcanvas show={drawer} onHide={() => setDrawer(false)} placement="start" className="d-lg-none">
              <Offcanvas.Header closeButton>
                <div className="d-flex align-items-center gap-2">
                  {logoNode}
                  <Offcanvas.Title className="text-truncate" style={{ maxWidth: 200 }}>
                    {schoolTitle}
                  </Offcanvas.Title>
                </div>
              </Offcanvas.Header>
              <Offcanvas.Body className="p-0 d-flex flex-column">
                <div className="px-2 py-2">
                  <SidebarNav onNavigate={() => setDrawer(false)} />
                </div>
                <div className="mt-auto p-2 border-top d-flex gap-2">
                  <Button
                    variant="outline-secondary"
                    className="flex-grow-1 rounded-3"
                    onClick={toggleTheme}
                    title={`Switch to ${theme === "light" ? "dark" : "light"} mode`}
                  >
                    <i className={`bi ${theme === "light" ? "bi-moon" : "bi-sun"}`} /> Theme
                  </Button>
                  <Button variant="outline-danger" className="flex-grow-1 rounded-3" onClick={confirmLogout}>
                    <i className="bi bi-box-arrow-right" /> Logout
                  </Button>
                </div>
              </Offcanvas.Body>
            </Offcanvas>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Effects & theming */}
      <style>{`
        :root{
          --accent: ${accent};
          --accent-04: ${withAlpha(accent, 0.04)};
          --accent-10: ${withAlpha(accent, 0.10)};
          --accent-18: ${withAlpha(accent, 0.18)};
          --accent-30: ${withAlpha(accent, 0.30)};
          --ring: var(--accent-30);
        }

        .glass-sidebar{
          backdrop-filter: blur(10px);
          background: color-mix(in oklab, var(--bs-body-bg) 86%, transparent);
        }
        .bg-waves{
          position: fixed; inset: 0; z-index: 0; pointer-events: none;
          background:
            radial-gradient(70rem 25rem at -15% -10%, var(--accent-10), transparent 60%),
            radial-gradient(70rem 25rem at 115% -10%, var(--accent-18), transparent 60%);
        }
        .layout-root > *{ z-index: 1; }

        /* Sidebar links */
        .sidebar-link{
          padding: .6rem .8rem;
          border: 1px solid transparent;
          transition: background .18s ease, transform .14s ease, border-color .18s ease, box-shadow .18s ease;
          overflow: hidden;
        }
        .sidebar-link .icon-chip{
          width: 34px; height: 34px; border-radius: 10px; flex: 0 0 34px;
          background: color-mix(in oklab, var(--accent-04) 70%, transparent);
          display: inline-flex; border: 1px solid color-mix(in oklab, var(--accent) 20%, var(--bs-border-color));
          transition: transform .14s ease, background .18s ease, border-color .18s ease;
          overflow: hidden;
        }
        .sidebar-link .icon-chip i{ opacity: .9; }
        .sidebar-link .label{ font-weight: 500; letter-spacing: .1px; }
        .sidebar-link:hover{
          transform: translateX(2px);
          background: var(--bs-tertiary-bg);
          border-color: var(--accent-18);
          box-shadow: 0 1px 0 var(--accent-10) inset;
        }
        .sidebar-link:hover .icon-chip{ transform: translateY(-1px) scale(1.02); }

        /* Active state + rail */
        .sidebar-link .active-rail{
          position: absolute; left: 6px; top: 18%; height: 64%; width: 4px; border-radius: 6px;
          background: transparent; transition: background .18s ease, width .18s ease;
        }
        .sidebar-link.active{
          background: color-mix(in oklab, var(--accent-04) 60%, transparent);
          border-color: var(--accent-30);
          box-shadow: inset 0 0 0 1px var(--accent-10), 0 6px 18px -12px var(--accent);
        }
        .sidebar-link.active .active-rail{ background: var(--accent); width: 6px; }

        /* Subtle moving shine */
        .sidebar-link .shine{
          content: ""; position: absolute; inset: 0; pointer-events:none;
          background: linear-gradient(90deg, transparent, color-mix(in oklab, #fff 14%, transparent), transparent);
          transform: translateX(-120%); opacity: 0; transition: opacity .2s ease;
        }
        .sidebar-link:hover .shine{ animation: shineMove 1.2s ease forwards; opacity: .75; }
        @keyframes shineMove { to { transform: translateX(120%); } }

        /* Ripple */
        .ripple{
          position:absolute; border-radius:50%;
          transform: scale(0); background: color-mix(in oklab, var(--accent) 25%, transparent);
          animation: ripple 700ms ease-out;
          pointer-events:none; opacity:.6;
        }
        @keyframes ripple { to { transform: scale(1); opacity: 0; } }

        /* Topbar */
        .topbar{
          height: 62px;
          background: color-mix(in oklab, var(--bs-body-bg) 92%, transparent);
          backdrop-filter: blur(10px);
        }
        .accent-bar{ height: 6px; background: linear-gradient(90deg, transparent, var(--accent-18), transparent); }

        .quick-input .input-group-text{ background: transparent; }
        .quick-input .form-control{ border-left: 0; }
        .quick-input .form-control:focus{ box-shadow: 0 0 0 .2rem var(--ring); }

        .content-shell{ max-width: 1440px; margin: 0 auto; width: 100%; }

        /* Accent controls */
        .accent-dot{ width: 22px; height: 22px; border-radius: 999px; border: 2px solid #0000; cursor: pointer; }
        .accent-dot:focus-visible{ outline: 2px solid var(--accent); }
        .accent-picker{ display: inline-flex; align-items: center; gap:.35rem; cursor: pointer; }
        .accent-picker input{ position: absolute; width:1px; height:1px; opacity:0; pointer-events:none; }
        .accent-picker .swatch{
          width:22px; height:22px; border-radius:999px; border:1px solid var(--bs-border-color);
          box-shadow: inset 0 0 0 2px #fff4;
        }
        .accent-pill{ width: 14px; height: 14px; border-radius: 6px; display:inline-block; }

        /* ───────── 3D PNG icon styling ───────── */
        .icon-chip .icon3d{
          width: 78%; height: 78%; object-fit: contain;
          filter: drop-shadow(0 4px 8px rgba(0,0,0,.18));
          animation: iconFloat 6s ease-in-out infinite;
          transform: translateY(1px);
        }
        @keyframes iconFloat { 0%,100%{ transform: translateY(1px);} 50%{ transform: translateY(-3px);} }
        .sidebar-link:hover .icon3d{ filter: drop-shadow(0 6px 12px rgba(0,0,0,.22)); }
        .sidebar-link.active .icon3d{ filter: drop-shadow(0 7px 16px rgba(0,0,0,.28)); }

        /* ───────── Accent-driven label/icon color polish ───────── */
        .sidebar-link .icon-chip, .sidebar-link .icon-chip i{ color: var(--accent); }
        .sidebar-link .label{
          color: color-mix(in oklab, var(--bs-body-color) 88%, var(--accent) 12%);
          transition: color .18s ease;
        }
        .sidebar-link:hover .label{
          color: color-mix(in oklab, var(--bs-body-color) 70%, var(--accent) 30%);
        }
        .sidebar-link:hover .icon-chip{
          background: color-mix(in oklab, var(--accent-10) 80%, transparent);
          border-color: var(--accent-30);
        }
        .sidebar-link.active .label{ color: var(--accent); font-weight: 600; }
        .sidebar-link.active .icon-chip{
          background: var(--accent-10);
          border-color: var(--accent-30);
          box-shadow: 0 6px 18px -12px var(--accent);
        }
        .topbar .text-body{
          color: color-mix(in oklab, var(--bs-body-color) 70%, var(--accent) 30%);
        }
      `}</style>
    </div>
  );
}
